import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";

import { SingleSelectInput, Text } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { GoalAlignmentValue, createFetchGoalsForAlignment } from "./shared";

export type SingleSelectGoalAlignmentProps = {
	value?: GoalAlignmentValue | null;
	onChange: (goal: GoalAlignmentValue | null) => void;
	fetchGoals?: (searchTerm: string) => Promise<GoalAlignmentValue[]>;
	errored?: boolean;
	disabled?: boolean;
	placeholder?: string;
	id?: string;
	"data-testid"?: string;
	isClearable?: boolean;
	dangerouslySetClassName?: string;
	virtualizationConfig?: {
		itemHeight: number;
		maxHeight: number;
	};
};

export const Single = ({
	value,
	onChange,
	fetchGoals: customFetchGoals,
	errored,
	disabled,
	placeholder = "Search for goals...",
	id,
	"data-testid": dataTestId,
	isClearable = true,
	dangerouslySetClassName,
	virtualizationConfig = {
		itemHeight: 50,
		maxHeight: 300,
	},
}: SingleSelectGoalAlignmentProps) => {
	const queryClient = useQueryClient();
	const [selectedGoal, setSelectedGoal] = useState<GoalAlignmentValue | undefined>(value ?? undefined);

	const fetchGoalOptions = useCallback(
		async (searchTerm: string): Promise<Option<GoalAlignmentValue>[]> => {
			try {
				let goals: GoalAlignmentValue[];

				if (customFetchGoals) {
					goals = await customFetchGoals(searchTerm);
				} else {
					const defaultFetch = createFetchGoalsForAlignment(queryClient);
					goals = await defaultFetch(searchTerm);
				}

				return goals.map((goal) => ({
					label: goal.name,
					value: goal,
				}));
			} catch (error) {
				console.error("Error fetching goals:", error);
				return [];
			}
		},
		[queryClient, customFetchGoals]
	);

	useEffect(() => {
		if (!value) {
			setSelectedGoal(undefined);
			return;
		}

		if (selectedGoal?.id === value.id) {
			return;
		}

		setSelectedGoal(value);
	}, [value, selectedGoal]);

	const handleChange = (newValue: GoalAlignmentValue | null) => {
		setSelectedGoal(newValue ?? undefined);
		onChange(newValue);
	};

	const renderGoalValue = useCallback(
		(goal: GoalAlignmentValue) => (
			<div className="flex min-w-0 items-center overflow-hidden">
				<Text
					size="paragraph-3"
					weight="regular"
					dangerouslySetClassName="overflow-hidden truncate text-ellipsis whitespace-nowrap"
				>
					({goal.status}) <span className="font-semibold">{goal.name}</span>
				</Text>
			</div>
		),
		[]
	);

	const renderGoalOption = useCallback((option: Option<GoalAlignmentValue>) => {
		const { color, name, assignee, status, progress } = option.value;

		const subtitleParts: string[] = [];
		if (assignee) {
			subtitleParts.push(assignee);
		}
		const subtitle = subtitleParts.join(" • ");

		return (
			<div className="flex h-[50px] items-center gap-2 overflow-hidden py-2 pr-2">
				<div className="flex items-center gap-2">
					<div className="relative h-6 w-6">
						<div
							className="absolute inset-0 rounded-full"
							style={{
								background: `conic-gradient(${color} ${progress}%, #E9EAEC ${progress}%)`,
							}}
						/>
						<div className="absolute inset-[6px] rounded-full bg-white" />
					</div>
				</div>
				<div className="flex min-w-0 flex-1 flex-col justify-center">
					<div className="flex min-w-0 items-center gap-1">
						<span className="flex-shrink-0">
							<Text size="paragraph-3" weight="regular">
								({status})
							</Text>
						</span>
						<span className="min-w-0 flex-1 truncate text-sm font-semibold">{name}</span>
					</div>
					{subtitle && <div className="min-w-0 truncate text-xs font-normal text-neutral-7">{subtitle}</div>}
				</div>
			</div>
		);
	}, []);

	return (
		<SingleSelectInput<GoalAlignmentValue>
			fetchOptions={fetchGoalOptions}
			value={selectedGoal}
			onChange={handleChange}
			selectionVariant="combobox"
			placeholder={placeholder}
			errored={errored}
			disabled={disabled}
			id={id}
			data-testid={dataTestId}
			isClearable={isClearable}
			renderValue={renderGoalValue}
			renderOption={renderGoalOption}
			dangerouslySetClassName={dangerouslySetClassName}
			virtualizationConfig={virtualizationConfig}
		/>
	);
};

Single.displayName = "SingleSelectGoalAlignment";
